import { Box, Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as ConfirmedIcon } from 'common/assets/images/confirmed_icon.svg';
import { ReactComponent as PlusIcon } from 'common/assets/images/Plus.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';
import { setPrompt } from 'common/store/reducer/promptBuilderReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { getPromptLocation } from '../../api';
import {
	IAIModelLocation,
	IPromptProps,
	IPromptTypeOptions,
} from '../../api/type-def';

import {
	TYPE_OPTIONS,
	PROMPT_OUTPUT_TITLE,
	PROMPT_OUTPUT_TITLE_SUB_HEADER,
	PROMPT_TYPE_TITLE,
	PROMPT_TYPE_TITLE_SUB_HEADER,
	PROMPT_TOOLKIT_TITLE,
} from '../PromptBuilderConstants';

import styles from './PromptSettings.module.scss';

const { primaryBlue, crateBtnTextColor } = styles;

const PromptSettings = () => {
	const dispatch = useDispatch();
	const { OK } = HTTP_STATUS_CODES;
	const prompt: IPromptProps = useSelector(
		(state: RootState) => state.promptBuilder.prompt,
	);

	const [location, setLocation] = useState<IAIModelLocation[]>([]);

	const aiModelOptions: any = useSelector(
		(state: RootState) => state.promptBuilder.aiModelOptions,
	);

	const handleAiRecipeSelect = (obj: IPromptTypeOptions) => {
		const key = obj.key.toLowerCase();
		onPromptChangeHandler('aiModelKey', key);
	};

	const getPromptLocationDetails = async (key: string) => {
		const response: any = await getPromptLocation(key);

		if (response?.statusCode === OK) {
			const locationList = response.responseBody.data;
			setLocation(locationList);
		} else {
			setLocation([]);
		}
	};

	const onPromptChangeHandler = (key: string, value: string) => {
		dispatch(setPrompt({ ...prompt, [key]: value.toLowerCase() }));
	};

	useEffect(() => {
		if (prompt.aiModelKey) {
			getPromptLocationDetails(prompt.aiModelKey);
		}
	}, [prompt.aiModelKey]);

	const promptType = prompt?.type?.toLowerCase();
	return (
		<Box className={styles['toolkit-selection']}>
			<Box className={styles['prompt-type']}>
				<Typography className={styles['prompt-heading']} variant="h6">
					{PROMPT_TYPE_TITLE}
					<span className={styles['select-label-asterisk']}>*</span>
				</Typography>
				<Typography variant="body2" className={styles['prompt-sub-heading']}>
					{PROMPT_TYPE_TITLE_SUB_HEADER}
				</Typography>
				<Box className={styles['prompt-type-selection']}>
					{Object.values(TYPE_OPTIONS).map((option) => (
						<ButtonComp
							key={option}
							data-testid={option.replaceAll(' ', '-')}
							btnType="secondary"
							disabled={!prompt?.is_draft}
							onClick={() => onPromptChangeHandler('type', option)}
							className={
								promptType === option.toLowerCase()
									? styles['toolkit-selected']
									: styles['toolkit-button']
							}
						>
							{promptType === option.toLowerCase() && (
								<ConfirmedIcon
									className={styles['confirmed-icon']}
									fill={primaryBlue}
								/>
							)}
							{toSentenceCase(option)}
						</ButtonComp>
					))}
				</Box>
			</Box>

			<Box className={styles.toolkit}>
				<Typography className={styles['prompt-heading']} variant="h6">
					{PROMPT_OUTPUT_TITLE}
					<span className={styles['select-label-asterisk']}>*</span>
				</Typography>
				<Typography variant="body2" className={styles['prompt-sub-heading']}>
					{PROMPT_OUTPUT_TITLE_SUB_HEADER}
				</Typography>
				<Box className={styles['ai-recipe-selection']}>
					{aiModelOptions.map((option: IPromptTypeOptions) => (
						<ButtonComp
							key={option.name}
							btnType="secondary"
							data-testid={option.name.replaceAll(' ', '-')}
							onClick={() => handleAiRecipeSelect(option)}
							disabled={!prompt?.is_draft}
							className={
								prompt.aiModelKey === option.key.toLowerCase()
									? styles['toolkit-selected']
									: styles['toolkit-button']
							}
						>
							{prompt.aiModelKey === option.key.toLowerCase() && (
								<ConfirmedIcon
									className={styles['confirmed-icon']}
									fill={primaryBlue}
								/>
							)}
							{toSentenceCase(option.name)}
						</ButtonComp>
					))}
					<ButtonComp
						btnType="text"
						color="primary"
						className={styles['new-prompt-output']}
						disabled
					>
						<PlusIcon
							className={styles['plus-icon']}
							fill={crateBtnTextColor}
						/>
						Create new
					</ButtonComp>
				</Box>
			</Box>

			{location.length > 0 ? (
				<Box className={styles['toolkit-access']}>
					<Typography variant="body2" className={styles['toolkit-heading']}>
						{PROMPT_TOOLKIT_TITLE}:
					</Typography>

					{location.map((option) => (
						<Box
							key={option.name}
							sx={{ display: 'flex', alignItems: 'center' }}
						>
							<Typography variant="body2" className={styles['toolkit-options']}>
								{option.name}
							</Typography>
						</Box>
					))}
				</Box>
			) : (
				<>&nbsp;</>
			)}
		</Box>
	);
};

export default PromptSettings;
